/**
 * Clerk mirror-integrity issues — everything the membership and organization
 * reconcilers say about our copy of Clerk.
 *
 * The `Issue` row IS the alarm: it carries the count, the runbook and the
 * auto-resolve, and the codes marked `page` post one line when they open. Every
 * code is `NEXUS` fault and admin-only on purpose. A customer cannot act on any
 * of them, and the failure they describe — our copy of Clerk drifting — is ours.
 *
 * Each sweep's verdict is a priority order (refused, then read-incomplete, then
 * the findings), so a verdict proves absent every condition the judge ruled out
 * before reaching it. That is what resolves these rows; an incomplete read rules
 * nothing out and resolves nothing.
 */
import type { IssueDefinition } from "../issue-definition";
import { ISSUE_CATEGORY, ISSUE_FAULT } from "../issue-enums";

export const CLERK_ISSUES = {
  CLERK_MEMBERSHIP_MIRROR_DRIFT: {
    category: ISSUE_CATEGORY.OBSERVABILITY,
    message:
      "The Clerk membership reconciler repaired this organization's mirror on three consecutive sweeps",
    severity: "high",
    // Pages: drift means the webhook path is losing deliveries right now, and this
    // is the alarm that would have caught a nineteen-month subscription gap on day one.
    page: true,
    fault: ISSUE_FAULT.NEXUS,
    remedy:
      "A healthy mirror reconciles to ZERO, because the webhook already did the work. Repeated repair means the reconciler is covering for a broken layer above it, and the reconciler is a backstop rather than a feeder.\n" +
      "1. Open the Clerk dashboard's webhook endpoint and compare its ticked events against `apps/backend/src/users/clerk-webhook-subscriptions.ts`. An unticked `organizationMembership.*` is the cause that has already happened once: `organizationMembership.created` was handled from 2024-12-30 and had still never been delivered on 2026-08-08, nineteen months.\n" +
      "2. If the subscription is present, read the endpoint's delivery log for that event type. Svix disables an endpoint after failures 12h apart on 5 consecutive days, so one permanently-failing type burns the fuse for every other type on the same endpoint.\n" +
      "3. Read the admin Issues page's rawPayload for the per-sweep counts. `inserted` means deliveries are being lost; `deleted` means revocations are being lost; `roleUpdated` means role changes are.\n" +
      "This issue resolves itself on the first sweep that repairs nothing."
  },
  CLERK_MEMBERSHIP_REVOCATION_BLOCKED: {
    category: ISSUE_CATEGORY.OBSERVABILITY,
    message:
      "The Clerk membership reconciler refused to delete memberships: the blast-radius breaker tripped",
    severity: "critical",
    // Pages: the likeliest cause is a pod talking to the wrong Clerk instance, which
    // a human has to rule out before anything else touches access.
    page: true,
    fault: ISSUE_FAULT.NEXUS,
    remedy:
      "The sweep wanted to remove more memberships than the breaker allows, so it removed NOTHING and marked NOTHING — a block cancels the whole downward direction, so no quarantine clock was started on these rows and none is running. A correct mass revocation is rare; a bug, a wrong Clerk instance, or a staging key pointed at production is not.\n" +
      "1. Confirm which Clerk instance the running pod is talking to BEFORE anything else. A staging secret key against the production database is the shape this breaker exists to catch, and it presents as a plausible mass revocation.\n" +
      '2. If the instance is right, read any rows an EARLIER complete sweep quarantined: `SELECT * FROM "OrganizationMembership" WHERE "absentFromClerkSince" IS NOT NULL`. This run added none of them. Compare a handful against the Clerk dashboard by hand.\n' +
      "3. If the revocations are genuine, remove them deliberately rather than by raising the breaker — the breaker is not a threshold to tune, it is a claim that a sweep this large is not routine.\n" +
      "Nothing has been deleted and no access has been removed. Whether the mirror still converged UPWARD depends on which block tripped: the blast-radius breaker keeps the inserts and role updates, while a first-run refusal cancels those too and writes nothing at all."
  },
  CLERK_MEMBERSHIP_RECONCILER_STALLED: {
    category: ISSUE_CATEGORY.OBSERVABILITY,
    message:
      "No Clerk membership sweep has completed within three cadences — the reconciler is not running",
    severity: "high",
    // Pages: the backstop itself is down, so every other code in this file has gone
    // silent with it. It is also where a Clerk read that KEEPS failing surfaces,
    // because an incomplete sweep never counts as a completed one.
    page: true,
    fault: ISSUE_FAULT.NEXUS,
    remedy:
      "The mirror is no longer being checked, so drift accumulates silently from now on. This is the failure the whole reconciler exists to prevent, one layer up.\n" +
      "1. Check the `clerk.reconciliation.memberships` cron in the admin System Config page. `enabled: false` logs one line and returns, which is exactly how the original defect reproduces.\n" +
      "2. Check the backend logs for `ClerkMembershipReconciliationService`. A sweep that starts and throws logs at the registration boundary and does not record a ledger row.\n" +
      "3. Check Clerk API reachability and the `CLERK_SECRET_KEY` in the running process. An unreachable Clerk makes every page fail, which makes every sweep incomplete — and an incomplete sweep is recorded, so this alarm firing means no sweep FINISHED at all.\n" +
      "The ledger is `ClerkMembershipReconciliationSweep`; its most recent `finishedAt` with `sweepComplete = true` is the number this check reads.\n" +
      "This issue resolves itself on the first liveness check that finds a recent complete sweep."
  },
  CLERK_MEMBERSHIP_SWEEP_READ_INCOMPLETE: {
    category: ISSUE_CATEGORY.OBSERVABILITY,
    message:
      "The Clerk membership sweep could not read every membership from Clerk, so nothing was quarantined or revoked this run",
    severity: "medium",
    fault: ISSUE_FAULT.NEXUS,
    // Not paged: a single incomplete read is usually a Clerk blip, and one that
    // persists stops sweeps completing, which CLERK_MEMBERSHIP_RECONCILER_STALLED pages.
    remedy:
      "The mirror still converged UPWARD on what Clerk did return; only the downward direction (quarantine, revocation) was skipped, because on a partial read the absent set is an artefact of the failure rather than evidence. Open breaker issues are left exactly as they were.\n" +
      "1. Read the failure reason on this issue's rawPayload. A pagination limit or a 5xx from Clerk is a Clerk-side or volume problem; a 401/403 is the secret key.\n" +
      "2. Check Clerk's status page and the `CLERK_SECRET_KEY` reaching the running pod.\n" +
      "This issue resolves itself on the first sweep whose read completes."
  },
  CLERK_ORGANIZATION_MIRROR_REPAIRED: {
    category: ISSUE_CATEGORY.OBSERVABILITY,
    message:
      "The Clerk organization reconciler had to create or rename organizations: an organization.created or organization.updated webhook was lost",
    severity: "high",
    fault: ISSUE_FAULT.NEXUS,
    // Pages: a healthy mirror reconciles to ZERO, and one lost organization.created
    // freezes every member of that organization until somebody finds it.
    page: true,
    remedy:
      "The repair itself already happened; what is broken is the webhook path that should have made it unnecessary.\n" +
      "1. Read rawPayload for `created` and `renamed`. Created means organization.created deliveries are being lost; renamed means organization.updated ones are.\n" +
      "2. Compare the Clerk webhook endpoint's ticked events against `apps/backend/src/users/clerk-webhook-subscriptions.ts`, and read the endpoint's delivery log for those types. Svix disables an endpoint after five consecutive days of failures, which takes every event type down with it.\n" +
      "This issue resolves itself on the first complete sweep that creates and renames nothing."
  },
  CLERK_ORGANIZATION_SURPLUS: {
    category: ISSUE_CATEGORY.OBSERVABILITY,
    message:
      "This organization exists in our database and NOT in Clerk — the shape a lost organization.deleted leaves",
    severity: "high",
    fault: ISSUE_FAULT.NEXUS,
    // Not paged: nothing worsens by the hour and nothing is deleted automatically,
    // but a customer's data may be retained after they removed their account.
    remedy:
      "Nothing was deleted and nothing will be — a teardown is not a reconciler's decision.\n" +
      "1. Look the organization id up in the Clerk dashboard. If Clerk really has no such organization, the customer deleted it and organization.deleted never reached us.\n" +
      "2. Confirm the deletion with the account owner before any teardown; then run the organization deletion flow deliberately.\n" +
      "3. Check the Clerk webhook endpoint's delivery log for organization.deleted, since the next one will be lost the same way.\n" +
      "This issue resolves itself once a complete sweep no longer finds this organization missing from Clerk."
  },
  CLERK_ORGANIZATION_REPAIR_REFUSED: {
    category: ISSUE_CATEGORY.OBSERVABILITY,
    message:
      "The Clerk organization reconciler refused to create organizations: the sweep wanted more than the breaker allows",
    severity: "critical",
    fault: ISSUE_FAULT.NEXUS,
    // Pages: a mass create is the shape of a staging Clerk key against the
    // production database, which a human must rule out first.
    page: true,
    remedy:
      "The sweep created NOTHING. A correct mass organization create is rare; a wrong Clerk instance is not.\n" +
      "1. Confirm which Clerk instance the running pod is talking to before anything else.\n" +
      "2. If the instance is right, read the reason and the counts on rawPayload and compare a handful of the missing organizations against the Clerk dashboard by hand.\n" +
      "3. Create genuine ones deliberately rather than by raising the breaker — it is a claim that a sweep this large is not routine, not a threshold to tune.\n" +
      "This issue resolves itself on the first sweep that is not refused and whose read completes."
  },
  CLERK_ORGANIZATION_SWEEP_READ_INCOMPLETE: {
    category: ISSUE_CATEGORY.OBSERVABILITY,
    message:
      "The Clerk organization sweep could not read every organization from Clerk, so nothing was reported missing this run",
    severity: "medium",
    fault: ISSUE_FAULT.NEXUS,
    // Not paged: usually a Clerk blip, and on a partial read the surplus list is
    // empty by construction, so the other codes are held rather than cleared.
    remedy:
      "The mirror still converged upward on the organizations Clerk did return.\n" +
      "1. Read the failure reason on rawPayload. A 5xx or a pagination limit is Clerk-side; a 401/403 is the secret key.\n" +
      "2. Check Clerk's status page and the `CLERK_SECRET_KEY` reaching the running pod.\n" +
      "This issue resolves itself on the first sweep whose read completes."
  },
  CLERK_ORGANIZATION_REPAIR_DEGRADED: {
    category: ISSUE_CATEGORY.OBSERVABILITY,
    message:
      "The Clerk organization sweep could not complete every repair: some creates were blocked on an unmirrored creator or failed",
    severity: "medium",
    fault: ISSUE_FAULT.NEXUS,
    // Not paged: every failed repair is retried on the next sweep by itself.
    remedy:
      "Read rawPayload's two lists.\n" +
      "1. `blockedOnUnmirroredCreator` — the organization's creator has no user row here, so no Stripe customer could be opened. That is a lost user.created, not an organization gap, and it is NOT repairable by this sweep: check the Clerk webhook's user.created deliveries.\n" +
      "2. `failed` — a create or rename threw for another reason; the backend log for `ClerkOrganizationReconciliationService` names the error. These retry every sweep.\n" +
      "This issue resolves itself on the first sweep that finds nothing to repair."
  }
} as const satisfies Record<string, IssueDefinition>;
